import { useCallback, useRef } from 'react';
import { ConvexMessage, MessageDocument, MessageImage, SearchItem } from '../types/message';
import { Mode } from '../types/mode';
import { useChatContext } from '../contexts/AppDataContext';
import { useMutation } from "convex/react";
import { useAuth } from '../contexts/AuthContext';
import { isTempMessageId } from '../utils/messageIds';
import { stripTransientStreamMarkup } from '../helpers/streamContent';

export interface MessageOperations {
  messages: ConvexMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ConvexMessage[]>>;
  insertNewMessage: (props: {
    isAi: boolean,
    messagePairId: string,
    branchId: number,
    _id: string,
    content: string,
    mode: Mode,
    textAttachments?: MessageDocument[],
    images?: MessageImage[]
  }) => Promise<void>;
  updateMessageId: (id: string, newId: string) => void;
  removeMessage: (id: string) => void;
  updateMessageContent: (id: string, newContent: string) => void;
  stopGeneration: (messageId: string) => Promise<void>;
  getMessagePairId: (messageId: string) => string | undefined;
  updateMessageSearchItems: (id: string, newSearchItems: SearchItem[]) => void;
  updateMessageTextAttachments: (id: string, newTextAttachments: MessageDocument[]) => void;
  updateMessageImages: (id: string, newImages: MessageImage[]) => void;
  updateMessageBranchId: (id: string, newBranchId: number) => void;
  updateMessagePairId: (id: string, newPairId: string) => void;
  updateMessageModel: (id: string, newModel: string) => void;
  updateMessageChatId: (newChatId: string) => void;
  locallyFinishGeneration: (messageId: string) => void;
  markMessageasStopped: (message: ConvexMessage) => void;
}

export const useMessageOperations = (
  messages: ConvexMessage[], 
  setMessages: React.Dispatch<React.SetStateAction<ConvexMessage[]>>
): MessageOperations => {
  const { currentChatId, setIsStreaming, isIncognito } = useChatContext();
  const { isAuthenticated, user } = useAuth();

  const updateMessageMutation = useMutation("messages:updateMessage" as any);

  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  
  const insertNewMessage = useCallback(async (props: {
    isAi: boolean, 
    messagePairId: string, 
    branchId: number, 
    _id: string, 
    content: string,
    mode: Mode,
    textAttachments?: MessageDocument[],
    images?: MessageImage[]
  }): Promise<void> => {
    const processedContent = !props.isAi ? `${props.content}\n<|endofinput|>\n` : props.content;
    
    const newMessage: ConvexMessage = {
      _id: props._id,
      _creationTime: Date.now(),
      content: processedContent,
      isAi: props.isAi,
      messagePairId: props.messagePairId,
      branchId: props.branchId || 1,
      timestamp: Date.now(),
      isGenerating: props.isAi,
      textAttachments: props.textAttachments || [],
      images: props.images || [],
      chatId: currentChatId || '',
      deletedAt: undefined,
      isDisliked: false,
      isInterrupted: false,
      isLiked: false,
      isStopped: false,
      mode: props.mode || null,
      model: null,
      searchItems: [],
      tool_calls: null,
      tool_results: null,
      userId: user?._id || undefined as any
    };
    
    setMessages((prevMessages: ConvexMessage[]) => [...prevMessages, newMessage]);
    

  }, [setMessages, currentChatId, user?._id]);

  const updateMessageId = useCallback((id: string, newId: string): void => {
    setMessages((prevMessages: ConvexMessage[]) => 
      prevMessages.map(message => 
        message._id === id ? { ...message, _id: newId } : message
      )
    );
  }, [setMessages]);

  const removeMessage = useCallback((id: string): void => {
    setMessages((prevMessages: ConvexMessage[]) => 
      prevMessages.filter(message => message._id !== id)
    );
  }, [setMessages]);

  const updateMessageContent = useCallback((id: string, newContent: string): void => {
    setMessages((prevMessages: ConvexMessage[]) => 
      prevMessages.map(message => {
        if (message._id === id) {
          const processedContent = !message.isAi ? `${newContent}\n<|endofinput|>\n` : newContent;
          return { ...message, content: processedContent };
        }
        return message;
      })
    );
  }, [setMessages]);

  const updateMessageBranchId = useCallback((id: string, newBranchId: number): void => {
    setMessages((prevMessages: ConvexMessage[]) => 
      prevMessages.map(message => 
        message._id === id ? { ...message, branchId: newBranchId } : message
      )
    );
  }, [setMessages]);

  const updateMessagePairId = useCallback((id: string, newPairId: string): void => {
    setMessages((prevMessages: ConvexMessage[]) => 
      prevMessages.map(message => 
        message._id === id ? { ...message, messagePairId: newPairId } : message
      )
    );
  }, [setMessages]);

  const updateMessageModel = useCallback((id: string, newModel: string): void => {
    setMessages((prevMessages: ConvexMessage[]) => 
      prevMessages.map(message => 
        message._id === id ? { ...message, model: newModel } : message
      )
    );
  }, [setMessages]);

  const updateMessageSearchItems = useCallback((id: string, newSearchItems: SearchItem[]): void => {
    setMessages(prevMessages => 
      prevMessages.map(message => 
        message._id === id
          ? {
              ...message,
              searchItems: [
                ...(message.searchItems ?? []),
                ...newSearchItems.filter(newItem => !(message.searchItems ?? []).some(prevItem => prevItem.url === newItem.url)),
              ],
            }
          : message,
      )
    );
  }, [setMessages]);

  const updateMessageTextAttachments = useCallback((id: string, newTextAttachments: MessageDocument[]): void => {
    setMessages(prevMessages => 
      prevMessages.map(message => 
        message._id === id
          ? {
              ...message,
              textAttachments: [
                ...(message.textAttachments ?? []),
                ...newTextAttachments.filter(attachment => !(message.textAttachments ?? []).some(prevAttachment => prevAttachment.title === attachment.title)),
              ],
            }
          : message,
      )
    );
  }, [setMessages]);

  const updateMessageImages = useCallback((id: string, newImages: MessageImage[]): void => {
    setMessages(prevMessages => 
      prevMessages.map(message => 
        message._id === id
          ? {
              ...message,
              images: [...newImages],
            }
          : message,
      )
    );
  }, [setMessages]);

  const updateMessageChatId = useCallback((newChatId: string): void => {
    setMessages(prevMessages => 
      prevMessages.map(message => 
        !message.chatId || message.chatId === 'new' ? { ...message, chatId: newChatId } : message,
      )
    );
  }, [setMessages]);

  const markMessageasStopped = useCallback((message: ConvexMessage): void => {
    // Incognito sessions never persist — flip local flags only and skip Convex.
    if (isIncognito) {
      setMessages((prevMessages: ConvexMessage[]) =>
        prevMessages.map(m =>
          m._id === message._id ? { ...m, isGenerating: false, isStopped: true } : m
        )
      );
      return;
    }
    if(isAuthenticated && user) {
      if (isTempMessageId(message._id)) return;
      updateMessageMutation({
        id: message._id,
        model: message.model || null,
        isGenerating: false,
        isStopped: true
      });
    }
  }, [updateMessageMutation, isAuthenticated, user, isIncognito, setMessages]);

  const locallyFinishGeneration = useCallback((messageId: string) => {
    setMessages((prevMessages: ConvexMessage[]) =>
      prevMessages.map(message =>
        message._id === messageId
          ? { ...message, isGenerating: false }
          : message
      )
    );
    setIsStreaming(false);
  }, [setMessages, setIsStreaming]);

  const stopGeneration = useCallback(async (messageId: string): Promise<void> => {
    const message = messagesRef.current.find(m => m._id === messageId);
    if (!message) return;
    const finalContent = message.isAi
      ? stripTransientStreamMarkup(message.content)
      : message.content;
    setMessages((prevMessages: ConvexMessage[]) => 
      prevMessages.map(m => 
        m._id === messageId
          ? { ...m, content: finalContent, isGenerating: false }
          : m
      )
    );
    markMessageasStopped({ ...message, content: finalContent });
    setIsStreaming(false)
  }, [setMessages, setIsStreaming, markMessageasStopped]);

  const getMessagePairId = useCallback((messageId: string): string | undefined => {
    return messagesRef.current.find(message => message._id === messageId)?.messagePairId || undefined;
  }, []);

  return {
    messages,
    setMessages,
    insertNewMessage,
    updateMessageId,
    removeMessage,
    updateMessageContent,
    updateMessageBranchId,
    updateMessagePairId,
    updateMessageModel,
    stopGeneration,
    getMessagePairId,
    updateMessageSearchItems,
    updateMessageTextAttachments,
    updateMessageImages,
    locallyFinishGeneration,
    updateMessageChatId,
    markMessageasStopped
  };
};
